import Card from "@mui/material/Card";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonBadge from "components/ArgonBadge";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import { useState, useEffect } from "react";
import ArgonButton from "components/ArgonButton";
import { Grid, IconButton, Chip, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import ApnaTabs from "components/ApnaTabs/ApnaTabs";
import api from "../../../utils/api";
import PaginationBar from "components/PaginationBar/PaginationBar";

function DemoManagement() {
  const navigate = useNavigate();
  const [demos, setDemos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const [statusFilter, setStatusFilter] = useState("All");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalDemos, setTotalDemos] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Status counts
  const [statusCounts, setStatusCounts] = useState({
    All: 0,
    SELECTED: 0,
    REQUESTED: 0,
    SCHEDULED: 0,
    COMPLETED: 0,
    CANCELLED: 0,
    REJECTED: 0,
    RESCHEDULED: 0,
  });

  // Fetch demos data with backend pagination
  const fetchDemos = async () => {
    try {
      setLoading(true);

      // Build query parameters
      const params = {
        page: currentPage,
        limit: rowsPerPage,
      };

      // Add status filter only if not "All"
      if (statusFilter !== "All") {
        params.status = statusFilter;
      }

      const response = await api.get(`/demo`, { params });

      console.log("Demo API Response:", response.data);

      if (response.data.success) {
        // Backend returns demos directly in response.data, not in response.data.data
        setDemos(response.data.demos || []);
        setTotalDemos(response.data.totalDemos || 0);
        setTotalPages(response.data.totalPages || 0);
      } else {
        console.error("API error:", response.data.message);
        setDemos([]);
        setTotalDemos(0);
        setTotalPages(0);
      }
    } catch (error) {
      console.error("Error fetching demos:", error);
      setDemos([]);
      setTotalDemos(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDemos();
  }, [refresh, currentPage, rowsPerPage, statusFilter]);

  // Fetch status counts separately
  useEffect(() => {
    fetchStatusCounts();
  }, [refresh]);

  const fetchStatusCounts = async () => {
    try {
      const statuses = [
        "SELECTED",
        "REQUESTED",
        "SCHEDULED",
        "DONE",
        "CANCELLED",
        "REJECTED",
        "RESCHEDULED",
      ];
      const counts = { All: 0 };

      // Fetch count for each status
      const promises = statuses.map(async (status) => {
        const response = await api.get(`/demo`, { params: { page: 1, limit: 1, status } });
        // Backend returns totalDemos directly in response.data, not in response.data.data
        return { status, count: response.data.totalDemos || 0 };
      });

      const results = await Promise.all(promises);

      results.forEach(({ status, count }) => {
        counts[status] = count;
        counts.All += count;
      });

      setStatusCounts(counts);
    } catch (error) {
      console.error("Error fetching status counts:", error);
    }
  };

  // Status badge color mapping
  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case "SELECTED":
        return "primary";
      case "REQUESTED":
        return "warning";
      case "SCHEDULED":
        return "info";
      case "DONE":
      case "COMPLETED":
        return "success";
      case "CANCELLED":
        return "error";
      case "REJECTED":
        return "error";
      case "RESCHEDULED":
        return "secondary";
      default:
        return "default";
    }
  };

  // Students List Component with hover functionality
  const StudentsList = ({ students = [], otm = false }) => {
    if (students.length === 0) {
      return (
        <ArgonTypography variant="caption" color="error">
          No Students
        </ArgonTypography>
      );
    }

    const displayStudent = students[0]; // first student
    const extraCount = students.length - 1; // extra students count

    const content = (
      <ArgonBox display="flex" alignItems="center" gap={1}>
        <ArgonBox display="flex" flexDirection="column" alignItems="flex-start">
          <ArgonBox display="flex" alignItems="center" gap={1}>
            <ArgonTypography
              textTransform="none"
              sx={{
                backgroundColor: "#f0f0f0",
                padding: "4px 7px",
                borderRadius: "6px",
              }}
              variant="caption"
              fontWeight="medium"
              color="dark"
            >
              {displayStudent} {extraCount > 0 && `+${extraCount}`}
            </ArgonTypography>
            {otm && (
              <Chip
                label="OTM"
                size="small"
                variant="filled"
                color="success"
                sx={{
                  fontSize: "0.65rem",
                  height: "20px",
                  fontWeight: "medium",
                  borderRadius: "6px",
                  "& .MuiChip-label": {
                    color: "#ffffff", // text white
                  },
                }}
              />
            )}
          </ArgonBox>
        </ArgonBox>
      </ArgonBox>
    );

    const tooltipContent = (
      <ArgonBox sx={{ maxHeight: "200px", overflowY: "auto", padding: "4px" }}>
        <ArgonTypography
          variant="caption"
          fontWeight="bold"
          color="white"
          sx={{ display: "block", marginBottom: "8px" }}
        >
          All Students ({students.length}):
        </ArgonTypography>
        <ArgonBox>
          {students.map((student, index) => (
            <ArgonTypography
              key={index}
              variant="caption"
              color="white"
              sx={{ display: "block", marginBottom: "4px" }}
            >
              • {student}
            </ArgonTypography>
          ))}
        </ArgonBox>
      </ArgonBox>
    );

    return (
      <Tooltip
        title={tooltipContent}
        arrow
        placement="top"
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.9)",
              backdropFilter: "blur(8px)",
              borderRadius: "8px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
              animation: "fadeInUp 0.3s ease-out",
              "@keyframes fadeInUp": {
                "0%": {
                  opacity: 0,
                  transform: "translateY(10px) scale(0.95)",
                },
                "100%": {
                  opacity: 1,
                  transform: "translateY(0) scale(1)",
                },
              },
            },
          },
          arrow: {
            sx: {
              color: "rgba(0, 0, 0, 0.9)",
            },
          },
        }}
      >
        <ArgonBox sx={{ cursor: "pointer" }}>{content}</ArgonBox>
      </Tooltip>
    );
  };

  StudentsList.propTypes = {
    students: PropTypes.arrayOf(PropTypes.string),
    otm: PropTypes.bool,
  };

  // Prepare table data (no client-side pagination, backend handles it)
  const getTableData = () => {
    const columns = [
      { name: "S.No", align: "center", width: "5%" },
      { name: "student", align: "left", width: "20%" },
      { name: "class Name", align: "left", width: "14%" },
      { name: "subject", align: "left", width: "14%" },
      { name: "board", align: "left", width: "14%" },
      { name: "shortlisted tutor", align: "left", width: "23%" },
      { name: "date", align: "center", width: "14%" },
      { name: "status", align: "center", width: "14%" },
      { name: "demo Type", align: "center", width: "12%" },
      { name: "actions", align: "left", width: "12%" },
    ];

    const rows = demos.map((demo, index) => {
      // Extract student names from demo.students array or fallback to tutorByStudent
      const studentNames = [];
      if (Array.isArray(demo.students) && demo.students.length > 0) {
        demo.students.forEach((student) => {
          const name = student?.name || student?.fullName || "Unknown";
          studentNames.push(name);
        });
      } else if (demo.tutorByStudent?.name) {
        studentNames.push(demo.tutorByStudent.name);
      }

      // Extract parent name
      const parentName = demo.parentId?.fullName || demo.parentId?.name || "";

      // Calculate serial number based on current page and rows per page
      const serialNumber = (currentPage - 1) * rowsPerPage + index + 1;

      return {
        "S.No": (
          <ArgonBox textAlign="center" px={2} py={0.5}>
            <ArgonTypography variant="caption" color="text" fontWeight="medium">
              {serialNumber}
            </ArgonTypography>
          </ArgonBox>
        ),
        student: (
          <ArgonBox display="flex" flexDirection="column" py={0.5}>
            <ArgonBox display="flex" alignItems="center">
              <StudentsList students={studentNames} otm={demo.OTM} />
            </ArgonBox>
            {parentName && (
              <ArgonTypography variant="caption" color="secondary" sx={{ mt: 0.5, ml: 0 }}>
                Client - {parentName}
              </ArgonTypography>
            )}
          </ArgonBox>
        ),
        "class Name": (
          <ArgonBox textAlign="left" px={2} py={0.5}>
            <ArgonTypography variant="caption" color="text" fontWeight="medium">
              {demo.students?.[0]?.class?.name || "N/A"}
            </ArgonTypography>
          </ArgonBox>
        ),
        subject: (
          <ArgonBox textAlign="left" px={2} py={0.5}>
            <ArgonTypography variant="caption" color="text" fontWeight="medium">
              {demo.students?.[0]?.subjects?.[0]?.name || "N/A"}
            </ArgonTypography>
          </ArgonBox>
        ),
        board: (
          <ArgonBox textAlign="left" px={2} py={0.5}>
            <ArgonTypography variant="caption" color="text" fontWeight="medium">
              {demo.students?.[0]?.board?.name || "N/A"}
            </ArgonTypography>
          </ArgonBox>
        ),
        "shortlisted tutor": (
          <ArgonBox display="flex" alignItems="center" py={0.5}>
            <ArgonBox display="flex" flexDirection="column" minWidth={0} flex={1}>
              <ArgonTypography variant="button" fontWeight="medium" noWrap>
                {demo.tutorByAdmin?.fullName || demo.tutorId?.fullName || "Not Assigned"}
              </ArgonTypography>
              <ArgonTypography variant="caption" color="secondary" noWrap>
                {demo.tutorByAdmin?.email || demo.tutorId?.email || "Admin pending"}
              </ArgonTypography>
            </ArgonBox>
          </ArgonBox>
        ),
        date: (
          <ArgonBox textAlign="center" px={2} py={0.5}>
            <ArgonTypography variant="caption" color="text" fontWeight="medium">
              {demo.demoDate || demo.preferedDate
                ? new Date(demo.demoDate || demo.preferedDate).toLocaleDateString()
                : "N/A"}
            </ArgonTypography>
          </ArgonBox>
        ),
        status: (
          <ArgonBox display="flex" justifyContent="center" px={2} py={0.5}>
            <ArgonBox position="relative" display="inline-flex">
              <ArgonBadge
                variant="gradient"
                badgeContent={
                  demo.demoStatus === "DONE" ? "COMPLETED" : demo.demoStatus || "Unknown"
                }
                color={getStatusColor(demo.demoStatus)}
                size="xs"
                container
              />
              {(demo.demoStatus === "DONE" || demo.demoStatus === "COMPLETED") &&
                demo.demofinalised === false && (
                  <ArgonTypography
                    component="span"
                    fontWeight="bold"
                    sx={{
                      position: "absolute",
                      top: "-12px",
                      right: "-9px",
                      color: "#fb8c00",
                      fontSize: "15px",
                      fontWeight: 800,
                      lineHeight: 1,
                    }}
                  >
                    {"\u20B9"}
                  </ArgonTypography>
                )}
            </ArgonBox>
          </ArgonBox>
        ),
        "demo Type": (
          <ArgonBox display="flex" justifyContent="center" px={2} py={0.5}>
            <Chip
              label={
                demo.demoType
                  ? demo.demoType.charAt(0).toUpperCase() + demo.demoType.slice(1).toLowerCase()
                  : "Online"
              }
              size="small"
              variant="outlined"
              color={
                demo.demoType?.toUpperCase() === "OFFLINE"
                  ? "secondary"
                  : demo.demoType?.toUpperCase() === "BOTH"
                  ? "success"
                  : "primary"
              }
              sx={{ fontSize: "0.75rem", height: "20px" }}
            />
          </ArgonBox>
        ),
        actions: (
          <ArgonBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={1}
            px={2}
            py={0.5}
          >
            <IconButton
              size="small"
              color="info"
              onClick={() => navigate(`/demos/view/${demo._id}`)}
              sx={{
                "&:hover": {
                  backgroundColor: "rgba(25, 118, 210, 0.08)",
                },
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
            {/* Hide Edit button when status is DONE (COMPLETED) */}
            {demo.demoStatus !== "DONE" && demo.demoStatus !== "COMPLETED" && (
              <IconButton
                size="small"
                color="warning"
                onClick={() => navigate(`/demos/edit/${demo._id}`)}
                sx={{
                  "&:hover": {
                    backgroundColor: "rgba(255, 152, 0, 0.08)",
                  },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </ArgonBox>
        ),
      };
    });

    return { columns, rows };
  };

  // Status tabs data - Show count from statusCounts state
  const getStatusCounts = () => {
    return [
      { label: "All", count: statusCounts.All, color: "#2027e1ff", status: "All" },

      { label: "Requested", count: statusCounts.REQUESTED, color: "#ff9800", status: "REQUESTED" },

      { label: "Selected", count: statusCounts.SELECTED, color: "#9c27b0", status: "SELECTED" },

      { label: "Scheduled", count: statusCounts.SCHEDULED, color: "#2196f3", status: "SCHEDULED" },

      {
        label: "Rescheduled",
        count: statusCounts.RESCHEDULED,
        color: "#9c27b0",
        status: "RESCHEDULED",
      },

      { label: "Completed", count: statusCounts.DONE, color: "#4caf50", status: "DONE" },

      { label: "Cancelled", count: statusCounts.CANCELLED, color: "#f44336", status: "CANCELLED" },

      { label: "Rejected", count: statusCounts.REJECTED, color: "#f44336", status: "REJECTED" },
    ];
  };

  const { columns, rows } = getTableData();
  const tabsData = getStatusCounts();

  // Handle pagination change
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1); // Reset to first page
  };

  // Reset to first page when filter changes
  const handleStatusFilterChange = (newStatus) => {
    setStatusFilter(newStatus);
    setCurrentPage(1); // Reset to first page when changing filter
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <ArgonTypography variant="h6" fontWeight="medium">
                  Demo Management
                </ArgonTypography>
                <ArgonBox display="flex" gap={1}>
                  <ArgonButton
                    variant="gradient"
                    color="success"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => navigate("/demos/create")}
                  >
                    Create Demo
                  </ArgonButton>
                  <ArgonButton
                    variant="outlined"
                    color="info"
                    size="small"
                    onClick={() => setRefresh((prev) => prev + 1)}
                  >
                    Refresh
                  </ArgonButton>
                </ArgonBox>
              </ArgonBox>

              {/* Status Tabs */}
              <ArgonBox mb={2}>
                <ApnaTabs tabsData={tabsData} onTabChange={handleStatusFilterChange} />
              </ArgonBox>
            </ArgonBox>

            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
                "& .MuiTableCell-root": {
                  padding: "8px 16px",
                  verticalAlign: "middle",
                },
                "& .MuiTableCell-head": {
                  fontWeight: "600",
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                  letterSpacing: "0.5px",
                },
              }}
            >
              {loading ? (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    Loading demos...
                  </ArgonTypography>
                </ArgonBox>
              ) : rows && rows.length > 0 ? (
                <>
                  <Table columns={columns} rows={rows} />

                  {/* Pagination Controls */}
                  <PaginationBar
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    totalItems={totalDemos}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                  />
                </>
              ) : (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="button" color="text">
                    {statusFilter === "All"
                      ? "No demos available. Create your first demo!"
                      : `No demos match the ${statusFilter} status`}
                  </ArgonTypography>
                </ArgonBox>
              )}
            </ArgonBox>
          </Card>
        </ArgonBox>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default DemoManagement;
